import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { staffPersonaQueryOptions, useStaffPersona } from "../queries";
import type { AvatarProps } from "@mantine/core";
import { getPortalsApiResourceUrl } from "@services/api/portalApi";
import {
	PhotoAvatar,
	signedPhotoSource,
	isPhotoIdentity,
} from "../photoLoading";

export const StaffAvatar: React.FC<{
	identifier: { value: string; type: string };
	size?: number;
	avatarProps?: AvatarProps;
}> = ({ identifier, size = 32, avatarProps }) => {
	const qc = useQueryClient();
	const params = {
		identifier: identifier.value,
		identifierType: identifier.type as "initials" | "staffId" | "oid",
	};
	const { data, isLoading } = useStaffPersona(params);
	const source = signedPhotoSource(
		data?.photo?.url ? getPortalsApiResourceUrl(data.photo.url) : undefined,
		async () => {
			const fresh = await qc.fetchQuery({
				...staffPersonaQueryOptions(params),
				staleTime: 0,
			});
			return fresh?.photo?.url
				? getPortalsApiResourceUrl(fresh.photo.url)
				: undefined;
		},
		size > 64 ? 256 : 96,
	);
	if (!isPhotoIdentity(identifier.value))
		return <PhotoAvatar size={size} {...avatarProps} />;
	if (isLoading || !data)
		return (
			<div
				style={{
					width: size,
					height: size,
					borderRadius: "50%",
					background: "rgba(0,0,0,0.08)",
				}}
			/>
		);
	return (
		<PhotoAvatar
			name={`${data.firstName} ${data.surname}`}
			color="initials"
			component="button"
			size={size}
			{...avatarProps}
			source={source}
		/>
	);
};
